import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AppHeader from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({
  onLogout,
  onShowScanner,
  onShowEarnings,
  onShowTastytrade,
  onShowAdmin,
  isAdmin,
}) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    country_code: '+1',
    phone_number: '',
    is_admin: false
  });
  
  const [newPassword, setNewPassword] = useState('');
  const username = localStorage.getItem('username');

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to fetch users');
      if (err.response?.status === 403) {
        alert('Admin privileges required');
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCreateModal(false);
      setNewUser({ 
        first_name: '', 
        last_name: '', 
        username: '', 
        password: '', 
        country_code: '+1',
        phone_number: '',
        is_admin: false 
      });
      fetchUsers();
      fetchStats();
      alert('User created successfully');
    } catch (err) {
      alert((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userid, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userid}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchUsers();
      fetchStats();
      setShowEditModal(false);
      setShowPasswordModal(false);
      alert('User updated successfully');
    } catch (err) {
      alert((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userid, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchUsers();
      fetchStats();
      alert('User deleted successfully');
    } catch (err) {
      alert((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to delete user');
    }
  };

  const getStatusBadge = (user) => {
    if (!user.is_approved) {
      return <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 font-semibold">Pending Approval</span>;
    }
    if (!user.is_active) {
      return <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 font-semibold">Disabled</span>;
    }
    return <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-semibold">Active</span>;
  };

  const headerTitle = null;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <AppHeader
        title={headerTitle}
        isAdmin={isAdmin !== undefined ? isAdmin : true}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowTastytrade={onShowTastytrade}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
      />

      {/* Main Content */}
      <div className="flex-1 p-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Admin Panel</h2>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total_users}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending_approval}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-green-600">{stats.active_users}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Inactive Users</p>
              <p className="text-2xl font-bold text-red-600">{stats.inactive_users}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Admin Users</p>
              <p className="text-2xl font-bold text-blue-600">{stats.admin_users}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
          >
            + Create New User
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.userid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">{user.username}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{user.first_name || '-'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{user.last_name || '-'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">
                          {user.country_code && user.phone_number 
                            ? `${user.country_code} ${user.phone_number}` 
                            : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_admin ? (
                          <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800 font-semibold">Admin</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">User</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {!user.is_approved && (
                            <button
                              onClick={() => handleUpdateUser(user.userid, { is_approved: true })}
                              className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition text-xs"
                            >
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => handleUpdateUser(user.userid, { is_active: !user.is_active })}
                            className={`px-3 py-1 rounded transition text-xs ${
                              user.is_active 
                                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                : 'bg-green-500 text-white hover:bg-green-600'
                            }`}
                          >
                            {user.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPasswordModal(true);
                            }}
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition text-xs"
                          >
                            Change Password
                          </button>
                          <button
                            onClick={() => handleUpdateUser(user.userid, { is_admin: !user.is_admin })}
                            className="bg-purple-500 text-white px-3 py-1 rounded hover:bg-purple-600 transition text-xs"
                          >
                            {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.userid, user.username)}
                            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Username/Email</label>
                <input
                  type="email"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newUser.country_code}
                    onChange={(e) => setNewUser({...newUser, country_code: e.target.value})}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+1"
                    required
                  />
                  <input
                    type="tel"
                    value={newUser.phone_number}
                    onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                    placeholder="Phone number"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUser.is_admin}
                    onChange={(e) => setNewUser({...newUser, is_admin: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Make this user an admin</span>
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUser({ 
                      first_name: '', 
                      last_name: '', 
                      username: '', 
                      password: '', 
                      country_code: '+1',
                      phone_number: '',
                      is_admin: false 
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Change Password for {selectedUser.username}</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateUser(selectedUser.userid, { password: newPassword });
              setNewPassword('');
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
